<template>
  <div class="station-level-analytics">
    <a-row :gutter="[16, 16]">
      <!-- Total Cycles Chart -->
      <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <a-card
          title="Total Number of Cycles"
          size="small"
          class="chart-card"
          :loading="loading"
        >
          <highcharts :options="getTotalCyclesChartOptions()" />
        </a-card>
      </a-col>

      <!-- Average Cycle Time Chart -->
      <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <a-card
          title="Average Cycle Time (seconds)"
          size="small"
          class="chart-card"
          :loading="loading"
        >
          <highcharts :options="getAvgCycleTimeChartOptions()" />
        </a-card>
      </a-col>

      <!-- Total Work Time Chart -->
      <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <a-card
          title="Total Work Time (seconds)"
          size="small"
          class="chart-card"
          :loading="loading"
        >
          <highcharts :options="getTotalWorkTimeChartOptions()" />
        </a-card>
      </a-col>

      <!-- Median Wait Time by Task Chart -->
      <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <a-card
          title="Median Wait Time by Task (seconds)"
          size="small"
          class="chart-card"
          :loading="loading"
        >
          <highcharts :options="getMedianWaitTimeChartOptions()" />
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue';

export default {
  name: 'StationLevelAnalytics',
  components: {
    highcharts: Chart,
  },
  props: {
    stationName: {
      type: String,
      default: 'Station 1',
    },
    analyticsData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Sample data structure matching API response
      sampleData: {
        labels: ['Engine Assembly', 'Quality Inspection', 'Final Testing'],
        total_cycles: [150, 145, 148],
        avg_cycle_time: [245.5, 180.25, 320.75],
        total_work_time: [36825.0, 26136.25, 47471.0],
        wait_time_by_previous_task: [25.3, 20.45, 22.9],
      },
    };
  },
  computed: {
    chartData() {
      return Object.keys(this.analyticsData).length > 0
        ? this.analyticsData
        : {
            labels: [],
            total_cycles: [],
            avg_cycle_time: [],
            total_work_time: [],
            wait_time_by_previous_task: [],
          };
    },
  },
  methods: {
    getTotalCyclesChartOptions() {
      return {
        chart: {
          type: 'column',
          height: 300,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: this.chartData.labels,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: 'Number of Cycles',
          },
          labels: {
            format: '{value}',
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: '{y}',
              style: {
                fontSize: '10px',
              },
            },
          },
        },
        colors: [
          '#5470c6',
          '#91cc75',
          '#fac858',
          '#ee6666',
          '#73c0de',
          '#3ba272',
          '#fc8452',
          '#9a60b4',
        ],
        series: [
          {
            name: 'Total Cycles',
            data: this.chartData.total_cycles,
          },
        ],
        credits: {
          enabled: false,
        },
        tooltip: {
          pointFormat: '<b>{point.y}</b> cycles',
        },
      };
    },

    getAvgCycleTimeChartOptions() {
      return {
        chart: {
          type: 'column',
          height: 300,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: this.chartData.labels,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: 'Time (seconds)',
          },
          labels: {
            format: '{value}s',
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: '{y:.1f}s',
              style: {
                fontSize: '10px',
              },
            },
          },
        },
        colors: [
          '#5470c6',
          '#91cc75',
          '#fac858',
          '#ee6666',
          '#73c0de',
          '#3ba272',
          '#fc8452',
          '#9a60b4',
        ],
        series: [
          {
            name: 'Average Cycle Time',
            data: this.chartData.avg_cycle_time,
          },
        ],
        credits: {
          enabled: false,
        },
        tooltip: {
          pointFormat: '<b>{point.y:.2f}</b> seconds',
        },
      };
    },

    getTotalWorkTimeChartOptions() {
      return {
        chart: {
          type: 'column',
          height: 300,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: this.chartData.labels,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: 'Time (seconds)',
          },
          labels: {
            format: '{value}s',
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: '{y:.0f}s',
              style: {
                fontSize: '10px',
              },
            },
          },
        },
        colors: [
          '#5470c6',
          '#91cc75',
          '#fac858',
          '#ee6666',
          '#73c0de',
          '#3ba272',
          '#fc8452',
          '#9a60b4',
        ],
        series: [
          {
            name: 'Total Work Time',
            data: this.chartData.total_work_time,
          },
        ],
        credits: {
          enabled: false,
        },
        tooltip: {
          pointFormat: '<b>{point.y:.0f}</b> seconds',
        },
      };
    },

    getMedianWaitTimeChartOptions() {
      return {
        chart: {
          type: 'column',
          height: 300,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: this.chartData.labels,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: 'Time (seconds)',
          },
          labels: {
            format: '{value}s',
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: '{y:.1f}s',
              style: {
                fontSize: '10px',
              },
            },
          },
        },
        colors: [
          '#5470c6',
          '#91cc75',
          '#fac858',
          '#ee6666',
          '#73c0de',
          '#3ba272',
          '#fc8452',
          '#9a60b4',
        ],
        series: [
          {
            name: 'Median Wait Time by Previous Task',
            data: this.chartData.wait_time_by_previous_task,
          },
        ],
        credits: {
          enabled: false,
        },
        tooltip: {
          pointFormat: '<b>{point.y:.2f}</b> seconds',
        },
      };
    },
  },
};
</script>

<style scoped>
.station-level-analytics {
  margin-bottom: 24px;
}

.chart-card {
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: box-shadow 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.station-level-analytics :deep(.ant-card) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}
</style>
